import * as React from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { TestIds } from "../testIds";

export function ResetPasswordPage(): JSX.Element {
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const params = new URLSearchParams(location.search);
  const token = params.get("token") || "";

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }

      try {
        const result = await api.post("/api/reset-password/validate", { token });

        setValidating(false);
        if (!api.isErrorToast(result) && "valid" in result && result.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
        }
      } catch {
        setValidating(false);
        setTokenValid(false);
      }
    }

    validateToken();
  }, [token]);

  async function submitResetPassword(): Promise<void> {
    setError("");

    if (!password) {
      setError("Please enter a password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    const result = await api.post("/api/reset-password", { token, password });

    if (!api.isErrorToast(result)) {
      setSubmitted(true);
    }
  }

  if (validating) {
    return (
      <div
        style={{
          padding: "40px 40px 0px 40px",
          backgroundColor: "#f7f7f9",
        }}
      >
        <div className="mx-auto max-w-[400px]">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800">
            Reset password
          </h2>
          <div className="rounded-lg border-b-[3px] border-[#e0dcdc] bg-white p-6 shadow-sm">
            <p className="text-gray-600">Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div
        style={{
          padding: "40px 40px 0px 40px",
          backgroundColor: "#f7f7f9",
        }}
      >
        <div className="mx-auto max-w-[400px]">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800">
            Reset password
          </h2>
          <div className="rounded-lg border-b-[3px] border-[#e0dcdc] bg-white p-6 shadow-sm text-center">
            <div className="mb-4 text-red-500">
              <i
                className="fa fa-times-circle text-4xl"
                aria-hidden="true"
              ></i>
            </div>
            <p className="mb-4 text-gray-700">
              This reset link is invalid or has expired. Please request a new
              password reset link.
            </p>
            <RLink
              className="text-blue-600 hover:underline"
              to="/forgot-password"
            >
              Request new link
            </RLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "40px 40px 0px 40px",
        backgroundColor: "#f7f7f9",
      }}
    >
      <div className="mx-auto max-w-[400px]">
        <h2 className="mb-4 text-2xl font-semibold text-gray-800">
          Reset password
        </h2>
        <div className="rounded-lg border-b-[3px] border-[#e0dcdc] bg-white p-6 shadow-sm">
          {submitted ? (
            <div className="text-center">
              <div className="mb-4 text-green-600">
                <i
                  className="fa fa-check-circle text-4xl"
                  aria-hidden="true"
                ></i>
              </div>
              <p className="mb-4 text-gray-700">
                Your password has been reset successfully.
              </p>
              <RLink className="text-blue-600 hover:underline" to="/login">
                Log in
              </RLink>
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-600">
                Enter your new password below.
              </p>

              {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  New password
                </label>
                <div className="relative">
                  <i
                    className="fa fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  ></i>
                  <input
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && submitResetPassword()
                    }
                    type="password"
                    name="password"
                    data-testid={TestIds.ResetPasswordInput}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter new password"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <div className="relative">
                  <i
                    className="fa fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  ></i>
                  <input
                    onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && submitResetPassword()
                    }
                    type="password"
                    name="confirmPassword"
                    data-testid={TestIds.ResetPasswordConfirmInput}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <button
                className="w-full rounded-md bg-[#33cb55] px-4 py-2 text-lg font-medium text-white shadow transition hover:brightness-105"
                data-testid={TestIds.ResetPasswordSubmit}
                onClick={() => submitResetPassword()}
              >
                Reset password
              </button>

              <div className="mt-4 text-center text-sm text-gray-600">
                Back to{" "}
                <RLink className="text-blue-600 hover:underline" to="/login">
                  Log in
                </RLink>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
